import { memo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, Search, Grid3X3, ShoppingCart, User } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { Badge } from "@/components/ui/badge";

interface NavItem {
  icon: typeof Home;
  label: string;
  href?: string;
  action?: "search" | "cart";
}

const navItems: NavItem[] = [
  { icon: Home, label: "Hem", href: "/" },
  { icon: Grid3X3, label: "Butik", href: "/kollektioner/alla-produkter" },
  { icon: Search, label: "Sök", action: "search" },
  { icon: ShoppingCart, label: "Varukorg", action: "cart" },
  { icon: User, label: "Konto", href: "/konto" },
];

interface MobileBottomNavProps {
  onSearchClick: () => void;
}

export const MobileBottomNav = memo(({ onSearchClick }: MobileBottomNavProps) => {
  const location = useLocation();
  const items = useCartStore((state) => state.items);
  const setCartOpen = useCartStore((state) => state.setIsOpen);
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  const handleClick = (item: NavItem) => {
    if (item.action === "search") {
      onSearchClick();
    } else if (item.action === "cart") {
      setCartOpen(true);
    }
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-card border-t border-border safe-area-bottom" role="navigation" aria-label="Mobilnavigering">
      <div className="flex items-center justify-around h-[60px]">
        {navItems.map((item) => {
          const isActive = item.href ? location.pathname === item.href : false;
          const Icon = item.icon;

          if (item.href) {
            return (
              <Link
                key={item.label}
                to={item.href}
                className={`flex flex-col items-center justify-center gap-1.5 w-full h-full min-h-[56px] px-2 transition-colors ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`}
                aria-current={isActive ? "page" : undefined}
              >
                <Icon className="w-6 h-6" />
                <span className="text-[11px] font-medium">{item.label}</span>
              </Link>
            );
          }

          return (
            <button
              key={item.label}
              onClick={() => handleClick(item)}
              className="relative flex flex-col items-center justify-center gap-1.5 w-full h-full min-h-[56px] px-2 text-muted-foreground transition-colors active:text-primary"
              aria-label={item.label}
            >
              <div className="relative">
                <Icon className="w-6 h-6" />
                {item.action === "cart" && totalItems > 0 && (
                  <Badge className="absolute -top-2.5 -right-2.5 h-5 w-5 p-0 flex items-center justify-center text-[10px] cta-gradient border-0">
                    {totalItems > 9 ? "9+" : totalItems}
                  </Badge>
                )}
              </div>
              <span className="text-[11px] font-medium">{item.label}</span>
            </button>
          );
        })}
      </div>
      {/* Safe area padding for iPhone home indicator */}
      <div className="h-[env(safe-area-inset-bottom)]" />
    </nav>
  );
});

MobileBottomNav.displayName = "MobileBottomNav";
